import React from 'react';
import { MeasureAnnotation } from '../types';

export type VoltaPosition = 'first' | 'middle' | 'last' | 'only';

interface MeasureAnnotationsProps {
  annotations: MeasureAnnotation | undefined;
  measureIndex: number;
  compact?: boolean; // For inline header display in Gig Mode
  fontSize?: number; // Dynamic font size (follows chord sizing)
  chartHasFine?: boolean; // Whether any measure in the chart has Fine
  chartHasToCoda?: boolean; // Whether any measure in the chart has To Coda
  voltaPosition?: VoltaPosition; // Position within a multi-measure volta bracket
}

const MeasureAnnotations: React.FC<MeasureAnnotationsProps> = ({ annotations, measureIndex, compact = false, fontSize = 12, chartHasFine = false, chartHasToCoda = false, voltaPosition }) => {
  if (!annotations || Object.keys(annotations).length === 0) {
    return compact ? null : (
      <div 
        className="h-6" 
        data-debug-id={`Level-3:EmptyAnnotations-${measureIndex + 1}`}
      />
    ); // Keep layout consistent in non-compact mode
  }

  const {
    section,
    ending,
    coda,
    segno,
    dalSegno,
    dalCapo,
    fine,
    toCoda,
    multipleEnding,
    rehearsalLetter,
    tempo,
    style,
    dynamics,
    customText
  } = annotations;

  // Compact mode for inline header display in Gig Mode
  if (compact) {
    // Dynamic font size for annotations: 50% of chord size, minimum 8px, maximum 16px
    const annotationFontSize = Math.max(8, Math.min(16, fontSize * 0.5));
    const fontSizeStyle = { fontSize: `${annotationFontSize}px`, lineHeight: '1.2' };
    
    return (
      <div className="flex items-center gap-1 text-gray-400 w-full relative" style={fontSizeStyle}>
        {/* Left-aligned section markers and ending numbers */}
        <div className="flex items-center gap-1">
          {section && <span className="border border-gray-400 px-0.5" style={{...fontSizeStyle, padding: '0 2px', lineHeight: '1'}}>{section}</span>}
          {rehearsalLetter && <span className="border border-blue-400 px-0.5 text-blue-400" style={{...fontSizeStyle, padding: '0 2px', lineHeight: '1'}}>{rehearsalLetter}</span>}
          
          {/* Ending numbers positioned immediately after section markers */}
          {ending && (
            <span className="text-gray-300 ml-1" style={fontSizeStyle}>{ending}.</span>
          )}
          {multipleEnding && (
            <span className="text-gray-300 ml-1" style={fontSizeStyle}>{multipleEnding.number.join(',')}</span>
          )}
        </div>
        
        {/* Center area for other annotations */}
        <div className="flex items-center gap-1 flex-1 justify-center">
          {segno && <span style={fontSizeStyle}>𝄋</span>}
          {coda && <span style={fontSizeStyle}>⊕</span>}
          {dalSegno && <span className="text-yellow-400" style={fontSizeStyle}>{chartHasToCoda ? 'D.S. al Coda' : chartHasFine ? 'D.S. al Fine' : 'D.S.'}</span>}
          {dalCapo && <span className="text-yellow-400" style={fontSizeStyle}>{chartHasToCoda ? 'D.C. al Coda' : chartHasFine ? 'D.C. al Fine' : 'D.C.'}</span>}
          {fine && <span className="text-red-400" style={fontSizeStyle}>Fine</span>}
          {toCoda && <span className="text-green-400" style={fontSizeStyle}>To Coda</span>}
          {tempo && <span className="text-purple-400" style={fontSizeStyle}>{tempo}</span>}
          {dynamics && <span className="text-orange-400" style={fontSizeStyle}>{dynamics}</span>}
          {customText && <span className="text-cyan-400" style={fontSizeStyle}>{customText}</span>}
        </div>
      </div>
    );
  }

  // Calculate if we need multiple rows for complex annotations
  const hasNavigationMarkers = dalSegno || dalCapo || fine || toCoda;
  const hasPerformanceMarkers = tempo || style || dynamics || customText;
  const needsMultipleRows = hasNavigationMarkers && hasPerformanceMarkers;

  return (
    <div 
      className={`${needsMultipleRows ? 'h-12' : 'h-6'} mb-1 text-gray-300 text-sm flex flex-col gap-1 relative`}
      data-debug-id={`Level-3:AnnotationContainer-${measureIndex + 1}`}
    >
      {/* Full-width ending brackets (volta) — rendered based on position within the bracket span */}
      {ending && !multipleEnding && (() => {
        // When voltaPosition is not provided, fall back to legacy 'only' behaviour
        const pos = voltaPosition ?? 'only';
        const showLeftBorder = pos === 'first' || pos === 'only';
        const showNumber    = pos === 'first' || pos === 'only';
        const showRightBorder = pos === 'last' || pos === 'only';
        const leftOffset = showNumber ? (section || rehearsalLetter ? 'left-12' : 'left-2') : 'left-0';
        return (
          <div
            className="absolute top-0 left-0 right-0 h-6 z-10"
            data-debug-id={`Level-3:EndingBracket-${measureIndex + 1}`}
          >
            {/* Top horizontal line */}
            <div className="absolute top-0 left-0 right-0 border-t-2 border-gray-500" />
            {/* Left vertical bar */}
            {showLeftBorder && (
              <div className="absolute left-0 top-0 bottom-0 border-l-2 border-gray-500" />
            )}
            {/* Ending number */}
            {showNumber && (
              <span className={`absolute top-0.5 font-bold text-sm leading-none text-gray-300 ${leftOffset}`}>
                {ending}.
              </span>
            )}
            {/* Right vertical bar */}
            {showRightBorder && (
              <div className="absolute right-0 top-0 bottom-0 border-r-2 border-gray-500" />
            )}
          </div>
        );
      })()}
      
      {multipleEnding && (
        <div 
          className="absolute top-0 left-0 right-0 z-10"
          data-debug-id={`Level-3:MultipleEndingBracket-${measureIndex + 1}`}
        >
          {/* Number positioned after section markers with inline underline */}
          <div className={`flex justify-start items-end ${section || rehearsalLetter ? 'ml-10' : 'ml-2'}`}>
            <span className="font-bold text-lg leading-none text-gray-300">
              {multipleEnding.number.join(', ')}.
            </span>
            <div className="flex-1 border-b border-gray-500 ml-0 mb-0"></div>
          </div>
          {/* Right border for closed endings */}
          {multipleEnding.type === 'closed' && (
            <div className="absolute top-0 bottom-0 right-0 border-r-2 border-gray-500"></div>
          )}
        </div>
      )}

      {/* Main annotation row */}
      <div 
        className="flex items-center justify-between gap-2 min-h-6"
        data-debug-id={`Level-3:MainAnnotationRow-${measureIndex + 1}`}
      >
        {/* Left side: Structural markers */}
        <div 
          className="flex items-center gap-2 flex-wrap"
          data-debug-id={`Level-3:LeftMarkers-${measureIndex + 1}`}
        >
          {/* Section marker */}
          {section && (
            <div 
              className="border border-gray-400 w-6 h-6 flex items-center justify-center font-bold text-base leading-none"
              data-debug-id={`Level-3:SectionMarker-${measureIndex + 1}`}
            >
              {section}
            </div>
          )}
          
          {/* Rehearsal letter */}
          {rehearsalLetter && (
            <div 
              className="border-2 border-blue-400 w-7 h-7 flex items-center justify-center font-bold text-blue-400 text-base leading-none"
              data-debug-id={`Level-3:RehearsalLetter-${measureIndex + 1}`}
            >
              {rehearsalLetter}
            </div>
          )}
          
          {/* Segno symbol */}
          {segno && (
            <div 
              className="flex items-center gap-1 font-bold text-yellow-400"
              data-debug-id={`Level-3:SegnoSymbol-${measureIndex + 1}`}
            >
              <span className="text-2xl leading-none">𝄋</span>
              <span className="text-xs">Segno</span>
            </div>
          )}
          
          {/* Coda symbol */}
          {coda && (
            <div 
              className="flex items-center gap-1 font-bold text-purple-400"
              data-debug-id={`Level-3:CodaSymbol-${measureIndex + 1}`}
            >
              <span className="text-xl leading-none">⊕</span>
              <span className="text-xs">Coda</span>
            </div>
          )}
          
          {/* Navigation instructions — compound labels based on chart context */}
          {dalSegno && (
            <div 
              className="bg-orange-600 px-2 py-1 rounded text-white font-bold text-xs"
              data-debug-id={`Level-3:DalSegno-${measureIndex + 1}`}
            >
              {chartHasToCoda ? 'D.S. al Coda' : chartHasFine ? 'D.S. al Fine' : 'D.S.'}
            </div>
          )}
          
          {dalCapo && (
            <div 
              className="bg-orange-600 px-2 py-1 rounded text-white font-bold text-xs"
              data-debug-id={`Level-3:DalCapo-${measureIndex + 1}`}
            >
              {chartHasToCoda ? 'D.C. al Coda' : chartHasFine ? 'D.C. al Fine' : 'D.C.'}
            </div>
          )}
          
          {fine && (
            <div 
              className="bg-red-600 px-2 py-1 rounded text-white font-bold text-xs"
              data-debug-id={`Level-3:Fine-${measureIndex + 1}`}
            >
              Fine
            </div>
          )}
          
          {toCoda && (
            <div 
              className="bg-purple-600 px-2 py-1 rounded text-white font-bold text-xs flex items-center gap-1"
              data-debug-id={`Level-3:ToCoda-${measureIndex + 1}`}
            >
              <span>To</span>
              <span className="text-sm">⊕</span>
            </div>
          )}
        </div>
        
        {/* Right side: No longer used for endings - they're now full-width */}
        <div 
          className="flex items-center gap-2"
          data-debug-id={`Level-3:RightMarkers-${measureIndex + 1}`}
        >
          {/* Space for other future right-aligned content */}
        </div>
      </div>
      
      {/* Secondary annotation row (for performance markers) */}
      {needsMultipleRows && (
        <div 
          className="flex items-center gap-3 text-xs opacity-90"
          data-debug-id={`Level-3:SecondaryRow-${measureIndex + 1}`}
        >
          {tempo && (
            <span 
              className="bg-blue-700 px-2 py-0.5 rounded text-white font-medium"
              data-debug-id={`Level-3:Tempo-${measureIndex + 1}`}
            >
              {tempo}
            </span>
          )}
          
          {style && (
            <span 
              className="bg-green-700 px-2 py-0.5 rounded text-white font-medium"
              data-debug-id={`Level-3:Style-${measureIndex + 1}`}
            >
              {style}
            </span>
          )}
          
          {dynamics && (
            <span 
              className="bg-indigo-700 px-2 py-0.5 rounded text-white font-medium italic"
              data-debug-id={`Level-3:Dynamics-${measureIndex + 1}`}
            >
              {dynamics}
            </span>
          )}
          
          {customText && (
            <span 
              className="text-gray-400 italic"
              data-debug-id={`Level-3:CustomText-${measureIndex + 1}`}
            >
              {customText}
            </span>
          )}
        </div>
      )}
      
      {/* Single row for simple performance markers */}
      {!needsMultipleRows && hasPerformanceMarkers && (
        <div className="flex items-center gap-3 text-xs opacity-90 absolute top-6 left-0">
          {tempo && (
            <span className="bg-blue-700 px-2 py-0.5 rounded text-white font-medium">
              {tempo}
            </span>
          )}
          
          {style && (
            <span className="bg-green-700 px-2 py-0.5 rounded text-white font-medium">
              {style}
            </span>
          )}
          
          {dynamics && (
            <span className="bg-indigo-700 px-2 py-0.5 rounded text-white font-medium italic">
              {dynamics}
            </span>
          )}
          
          {customText && (
            <span className="text-gray-400 italic">
              {customText}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default MeasureAnnotations;